import {OPCOES_MODELO_IA_V3, OpcaoCampoMia, formateDuracao, formateLista, opcoesDoCampo, CATALOGO_CONFIG_MIA, GRUPOS_CONFIG_MIA, MomentoDaConversa, campoDoCatalogo, campoDisponivel} from "./CatalogoConfigMia";

/**
 * O texto corrido da tela "Jeito de atender 2": um parágrafo por momento da
 * conversa e, dentro dele, uma frase por ASSUNTO. Um assunto junta os campos do
 * catálogo que só fazem sentido lidos juntos (o cardápio: como entrega + só do
 * dia + divulgar o link) e os escreve numa oração só. Clicar na frase abre os
 * controles daqueles campos; mudar um valor reescreve a frase.
 *
 * As frases afirmam comportamento ("ignora perguntas fora do atendimento"), então
 * precisam continuar verdadeiras quando a Mia muda — quem mexe no agente mexe aqui.
 */

/** O que as frases enxergam: a config mais o estado de edição da tela (escadas como arrays, nomes do upsell). */
export interface VistaDoJeito {
  [chave: string]: any;
  toquesCarrinhoMinutos: number[];
  toquesDescobertaMinutos: number[];
  upsellNomes: string[];
}

export type TipoControle = 'switch' | 'escolha' | 'numero' | 'texto' | 'area' | 'escada' | 'produtos' | 'notificacao' | 'avancado';

export interface ControleDoAssunto {
  tipo: TipoControle;
  /** Chave em ConfiguracoesMia; nas escadas é a chave do array na tela. */
  campo: string;
  rotulo: string;
  dica?: string;
  opcoes?: OpcaoCampoMia[];
  unidade?: string;
  maxLength?: number;
  placeholder?: string;
}

export type MomentoDoJeito = MomentoDaConversa;

export interface AssuntoDoJeito {
  id: string;
  /** Título curto do bloco no formulário ao lado do texto. */
  titulo: string;
  /** Ícone e cor do ladrilho, na tela em cartões. */
  emoji: string;
  cor: 'amarelo' | 'azul' | 'verde' | 'roxo' | 'laranja' | 'rosa' | 'teal' | 'cinza';
  /** Campos do catálogo que esta frase cobre (o spec exige cobertura total). */
  campos: string[];
  /** A frase inteira só existe nesta condição (ex.: carrinho ligado). */
  so?: (v: VistaDoJeito) => boolean;
  texto: (v: VistaDoJeito) => string;
  controles: (v: VistaDoJeito) => Array<ControleDoAssunto | false | null | undefined>;
  explicacao: string;
  /** Trechos da frase que são valores configurados (nome, tempos, textos) — a tela em cartões destaca. */
  destaques?: (v: VistaDoJeito) => string[];
}

export const PARAGRAFOS_DO_JEITO = GRUPOS_CONFIG_MIA.filter((grupo) => grupo.chave !== 'motor')
  .map((grupo) => ({momento: grupo.chave, titulo: grupo.rotulo}));

export const TITULO_ADMIN = 'Motor (admin)';

function corta(texto: string, n: number): string {
  const t = String(texto || '');
  return t.length > n ? t.slice(0, n - 1) + '…' : t;
}

function nomeDoModelo(valor: string): string {
  const opcao = OPCOES_MODELO_IA_V3.find((o) => o.valor === (valor || ''));
  return valor ? (opcao?.nome || valor).replace(/ — .*$/, '') : 'o modelo padrão do serviço';
}

export const ASSUNTOS_DO_JEITO: AssuntoDoJeito[] = [
  // ───────────── chegada ─────────────
  {
    id: 'nome', titulo: 'Nome da atendente', emoji: '👋', cor: 'amarelo', campos: ['nome'],
    texto: (v) => `Se apresenta como ${v.nome || '…'} 👋.`,
    controles: () => [{tipo: 'texto', campo: 'nome', rotulo: 'Nome da atendente', maxLength: 30}],
    destaques: (v) => [v.nome || '…'],
    explicacao: 'Nome exibido na saudação inicial.',
  },
  {
    id: 'perfil', titulo: 'Cliente conhecido', emoji: '⭐', cor: 'amarelo', campos: ['personalizarSaudacao', 'repetirPedido'],
    texto: (v) => {
      // o "não" é dito: frase muda de sentido quando um ajuste desliga, nunca só encurta
      const partes: string[] = [v.personalizarSaudacao ? 'cumprimenta pelo nome 😊' : 'não cumprimenta pelo nome'];
      if (campoDisponivel('conversaCurta', v)) partes.push(v.repetirPedido ? 'oferece repetir o último pedido' : 'não oferece repetir o último pedido');
      return `Com quem já pediu, ${formateLista(partes)}. Quem é novo recebe a apresentação completa.`;
    },
    controles: (v) => [
      {tipo: 'switch', campo: 'personalizarSaudacao', rotulo: 'Cumprimentar pelo nome, sem a apresentação completa'},
      campoDisponivel('conversaCurta', v) && {tipo: 'switch', campo: 'repetirPedido', rotulo: 'Oferecer repetir o último pedido', dica: 'desligue se o cardápio muda todo dia'},
    ],
    explicacao: 'O perfil vem do histórico da loja, inclusive dos pedidos pelo link, anteriores à Mia. Tudo é sugestão: o que o cliente pedir na conversa vale mais que o costume.',
  },
  {
    id: 'equipe', titulo: 'Chamar a equipe', emoji: '⏱️', cor: 'rosa', campos: ['escalarParaAtendente', 'mensagemEscalacao'],
    texto: (v) => v.escalarParaAtendente
      ? `Se o cliente chama um atendente, reclama ou a conversa trava, ${v.mensagemEscalacao ? `avisa “${corta(v.mensagemEscalacao, 48)}”` : 'avisa que vai chamar alguém'} e passa a conversa para sua equipe 🙋.`
      : 'Nunca passa a conversa para a equipe: resolve por conta própria.',
    controles: (v) => [
      {tipo: 'switch', campo: 'escalarParaAtendente', rotulo: 'Deixar a Mia passar a conversa para um atendente', dica: 'desligue se ninguém olha o painel'},
      v.escalarParaAtendente && {tipo: 'texto', campo: 'mensagemEscalacao', rotulo: 'Aviso ao cliente (em branco = palavras da Mia)', maxLength: 500, placeholder: 'Já passei sua mensagem para nossa equipe…'},
    ],
    destaques: (v) => v.escalarParaAtendente ? [...(v.mensagemEscalacao ? [`“${corta(v.mensagemEscalacao, 48)}”`] : [])] : [],
    explicacao: 'A conversa vai para a tela de conversas e alguém do time assume. Desligado, a Mia nunca promete um atendente que não vem. Evite prometer prazo no aviso.',
  },

  {
    id: 'pausa', titulo: 'Retorno após atendimento humano', emoji: '⏱️', cor: 'rosa', campos: ['tempoPausaMia'],
    texto: (v) => `Quando a pausa é temporária, volta após ${formateDuracao(v.tempoPausaMia)}. Carrinho em atendimento fica com a equipe até ser liberado.`,
    controles: () => [{tipo: 'numero', campo: 'tempoPausaMia', rotulo: 'Prazo de retorno da pausa temporária', unidade: 'segundos'}],
    explicacao: 'Vale também quando uma pessoa assume a conversa por conta própria, mesmo com a transferência automática desligada.',
  },

  // ───────────── descoberta ─────────────
  {
    id: 'cardapio', titulo: 'Como manda o cardápio', emoji: '📖', cor: 'azul', campos: ['entregaCardapio', 'imagemCardapioSoDoDia', 'divulgarLinkCardapio'],
    texto: (v) => {
      const como = ({LINK: 'por link 🔗', IMAGEM: 'em foto 📷', IMAGEM_E_LINK: 'em foto 📷 e depois por link'} as any)[v.entregaCardapio] || 'por link 🔗';
      const divulgacao = v.divulgarLinkCardapio ? ' Divulga o link na primeira consulta ao cardápio e menciona esse caminho na saudação.' : ' Só divulga o link quando o cliente pede explicitamente.';
      const dia = v.entregaCardapio !== 'LINK' && v.imagemCardapioSoDoDia ? ' Só manda a foto se ela foi trocada hoje.' : '';
      return `Quando o cliente pede, manda o cardápio ${como}.${dia}${divulgacao}`;
    },
    controles: (v) => [
      {tipo: 'escolha', campo: 'entregaCardapio', rotulo: 'Quando o cliente pede o cardápio', opcoes: opcoesDoCampo('entregaCardapio')},
      v.entregaCardapio !== 'LINK' && {tipo: 'switch', campo: 'imagemCardapioSoDoDia', rotulo: 'Só mandar a foto se ela foi atualizada hoje', dica: 'cardápio do dia'},
      {tipo: 'switch', campo: 'divulgarLinkCardapio', rotulo: 'Divulgar o link já na primeira vez'},
    ],
    explicacao: 'A foto é a mesma do botão “Enviar Imagem do Cardápio”, cadastrada em Cardápio. Sem foto cadastrada, vai o link.',
  },
  {
    id: 'botao', titulo: 'Botão do cardápio', emoji: '🔗', cor: 'verde', campos: ['rotuloBotaoCardapio', 'rodapeBotaoCardapio'],
    texto: (v) => `O botão do cardápio se chama “${v.rotuloBotaoCardapio || 'Fazer Pedido'}” e vem com a frase “${v.rodapeBotaoCardapio || 'Toque no botão para fazer seu pedido'}”.`,
    controles: () => [
      {tipo: 'texto', campo: 'rotuloBotaoCardapio', rotulo: 'Texto do botão (até 20)', maxLength: 20, placeholder: 'Fazer Pedido'},
      {tipo: 'texto', campo: 'rodapeBotaoCardapio', rotulo: 'Frase de apoio (até 60)', maxLength: 60, placeholder: 'Toque no botão para fazer seu pedido'},
    ],
    destaques: (v) => [`“${v.rotuloBotaoCardapio || 'Fazer Pedido'}”`, `“${v.rodapeBotaoCardapio || 'Toque no botão para fazer seu pedido'}”`],
    explicacao: 'Limites do WhatsApp: 20 caracteres no botão e 60 na frase de apoio.',
  },
  {
    id: 'produtos', titulo: 'Perguntas e produtos', emoji: '🍽️', cor: 'laranja', campos: ['responderSobreProdutos', 'comportamentoForaDoEscopo'],
    texto: (v) => {
      const a = v.responderSobreProdutos ? 'Responde sobre itens, preços e disponibilidade 🍽️' : 'Não responde sobre itens e preços, só direciona ao cardápio 🍽️';
      const b = ({RESPONDER: 'responde mesmo assim', NAO_RESPONDER: 'ignora', CHAMAR_ATENDENTE: 'passa para a equipe'} as any)[v.comportamentoForaDoEscopo] || 'ainda não tem regra';
      return `No bot antigo: ${a.charAt(0).toLowerCase() + a.slice(1)}. Perguntas fora do assunto da loja: ${b}. Estes ajustes não alteram a Mia V3.`;
    },
    controles: () => [
      {tipo: 'switch', campo: 'responderSobreProdutos', rotulo: 'Responder sobre produtos do cardápio'},
      {tipo: 'escolha', campo: 'comportamentoForaDoEscopo', rotulo: 'Perguntas fora do escopo', opcoes: opcoesDoCampo('comportamentoForaDoEscopo')},
    ],
    explicacao: 'Atenção: a Mia V3 ainda não lê estes dois ajustes — valem para o bot antigo.',
  },
  {
    id: 'midia', titulo: 'Áudio e fotos', emoji: '🎙️', cor: 'roxo', campos: ['entenderImagens', 'comportamentoAudio'],
    texto: (v) => {
      const a = v.entenderImagens ? 'Entende as fotos e prints que o cliente manda 🖼️' : 'Não interpreta fotos e prints 🖼️';
      const audio = campoDoCatalogo('comportamentoAudio')!.frase(v.comportamentoAudio, v);
      return `${a}. ${audio}`;
    },
    controles: (v) => [
      {tipo: 'escolha', campo: 'comportamentoAudio', rotulo: 'Quando o cliente manda áudio', opcoes: opcoesDoCampo('comportamentoAudio', v)},
      {tipo: 'switch', campo: 'entenderImagens', rotulo: 'Entender as imagens que o cliente manda'},
    ],
    explicacao: 'A transferência de áudio depende de Atendimento humano. Se estiver desligada, os modos Pedir para escrever e Chamar um atendente pedem texto uma vez e ficam em silêncio na insistência.',
  },

  // ───────────── montagem ─────────────
  {
    id: 'montagem', titulo: 'Montagem do produto', emoji: '🧩', cor: 'azul', campos: ['montagemProduto'],
    texto: (v) => {
      const onde = v.montagemProduto === 'PAGINA' ? 'numa página, por link' : 'no próprio chat, um grupo por vez';
      return `Produtos com várias escolhas são montados ${onde}.`;
    },
    controles: (v) => [
      {tipo: 'escolha', campo: 'montagemProduto', rotulo: 'Produto com várias escolhas', opcoes: opcoesDoCampo('montagemProduto')},
    ],
    explicacao: 'Vale para produtos em que um grupo aceita mais de uma opção (duas proteínas, três acompanhamentos) ou com muitos grupos.',
  },

  {
    id: 'respostas', titulo: 'Respostas da Mia', emoji: '💬', cor: 'roxo', campos: ['conversaCurta'],
    texto: (v) => campoDoCatalogo('conversaCurta')!.frase(v.conversaCurta, v),
    controles: () => [{tipo: 'switch', campo: 'conversaCurta', rotulo: 'Conversa curta', dica: 'resumos curtos e menos perguntas repetidas'}],
    explicacao: 'Afeta as respostas durante o pedido e a passagem ao fechamento. O cliente continua revisando antes de confirmar.',
  },

  // ───────────── fechamento ─────────────
  {
    id: 'endereco', titulo: 'Entrega e pagamento', emoji: '📍', cor: 'rosa', campos: ['pedirLocalizacao', 'sugerirDadosHabituais'],
    texto: (v) => {
      const pede = v.pedirLocalizacao ? 'a localização 📍' : 'o endereço digitado (rua, número e bairro)';
      const sugere = !campoDisponivel('conversaCurta', v) ? '' : v.sugerirDadosHabituais
        ? ' Para quem já pediu, sugere o endereço e o pagamento de sempre.'
        : ' Não sugere o endereço nem o pagamento de sempre: o cliente escolhe do zero.';
      return `Na entrega, pede ${pede}.${sugere}`;
    },
    controles: (v) => [
      {tipo: 'switch', campo: 'pedirLocalizacao', rotulo: 'Pedir a localização no delivery'},
      campoDisponivel('conversaCurta', v) && {tipo: 'switch', campo: 'sugerirDadosHabituais', rotulo: 'Sugerir os dados de sempre no fechamento'},
    ],
    explicacao: 'Se o cliente mandar a localização por conta própria, ela continua sendo aproveitada. Nada é escolhido sem o toque do cliente, e o troco é perguntado toda vez.',
  },
  {
    id: 'upsell', titulo: 'Upsell (oferecer algo a mais)', emoji: '🍰', cor: 'laranja', campos: ['upsellFechamento', 'upsellItens'], so: (v) => !!campoDisponivel('conversaCurta', v),
    // "upsell" é o termo que o mercado usa; a frase diz o que ela faz, quando e o que oferece
    texto: (v) => v.upsellFechamento
      ? `Faz upsell 🍮: quando o cliente termina de montar o pedido, oferece um item a mais, uma vez só. ${v.upsellNomes.length
        ? `Oferece ${v.upsellNomes.length === 1 ? 'este item' : 'um destes itens'}: ${formateLista(v.upsellNomes)}.`
        : 'Ela mesma escolhe o item, pelo que os clientes mais pedem junto.'}`
      : 'Não faz upsell: fecha o pedido sem oferecer nada a mais.',
    controles: (v) => [
      {tipo: 'switch', campo: 'upsellFechamento', rotulo: 'Upsell: oferecer um item a mais antes de fechar', dica: 'vende mais'},
      v.upsellFechamento && {tipo: 'produtos', campo: 'upsellItens', rotulo: 'Itens que a Mia vai oferecer (até 3, na ordem; vazio = ela escolhe)'},
    ],
    destaques: (v) => v.upsellFechamento ? v.upsellNomes : [],
    explicacao: 'Upsell é oferecer algo a mais na hora de fechar: sobremesa, bebida, acompanhamento. Se o cliente aceitar, o item entra direto no carrinho com o preço do cardápio. Sem itens escolhidos, a Mia oferece o que os clientes mais pedem junto.',
  },

  // ───────────── depois ─────────────
  {
    id: 'followup', titulo: 'Lembretes', emoji: '⏰', cor: 'roxo', campos: ['followupMiaV3', 'toquesCarrinhoMinutos', 'toquesDescobertaMinutos'], so: (v) => !!campoDisponivel('conversaCurta', v),
    texto: (v) => v.followupMiaV3
      ? `Manda lembrete ⏰ a quem deixou o pedido parado, aos ${formateLista(v.toquesCarrinhoMinutos)} minutos; a quem só conversou, aos ${formateLista(v.toquesDescobertaMinutos)}.`
      : 'Não manda lembrete a quem some no meio da compra.',
    controles: (v) => [
      {tipo: 'switch', campo: 'followupMiaV3', rotulo: 'Enviar lembretes a quem some', dica: 'recupera vendas'},
      v.followupMiaV3 && {tipo: 'escada', campo: 'toquesCarrinhoMinutos', rotulo: 'Toques do pedido parado (minutos, crescentes)'},
      v.followupMiaV3 && {tipo: 'escada', campo: 'toquesDescobertaMinutos', rotulo: 'Toques da conversa (minutos, crescentes)'},
    ],
    destaques: (v) => v.followupMiaV3 ? [`${formateLista(v.toquesCarrinhoMinutos)} minutos`, `aos ${formateLista(v.toquesDescobertaMinutos)}`] : [],
    explicacao: 'A mensagem só sai com a Mia ativa, a loja recebendo pedidos e nenhuma resposta ou pedido novo. Contados de quando o cliente parou de responder.',
  },
  {
    id: 'carrinho', titulo: 'Carrinho do cardápio digital', emoji: '🛒', cor: 'teal', campos: ['recuperarCarrinho', 'tempoRecuperarCarrinho'],
    texto: (v) => v.recuperarCarrinho
      ? `No caminho antigo, lembra quem abandonou o carrinho do cardápio digital, ${formateDuracao(v.tempoRecuperarCarrinho)} depois.`
      : 'No caminho antigo, não lembra quem abandonou o carrinho do cardápio digital.',
    controles: (v) => [
      {tipo: 'switch', campo: 'recuperarCarrinho', rotulo: 'Lembrar do carrinho abandonado'},
      v.recuperarCarrinho && {tipo: 'numero', campo: 'tempoRecuperarCarrinho', rotulo: 'Esperar antes de enviar', unidade: 'segundos'},
      v.recuperarCarrinho && {tipo: 'notificacao', campo: 'notificacaoCarrinho', rotulo: 'Mensagem enviada'},
    ],
    destaques: (v) => v.recuperarCarrinho ? [formateDuracao(v.tempoRecuperarCarrinho)] : [],
    explicacao: 'Atenção: a Mia V3 ainda não lê este ajuste — vale para o carrinho do cardápio digital.',
  },

  {
    id: 'fluxo', titulo: 'Menu do bot antigo', emoji: '⚙️', cor: 'cinza', campos: ['usarFluxoTypebot'],
    texto: (v) => `O menu guiado do bot antigo está ${v.usarFluxoTypebot ? 'ligado' : 'desligado'}. Não altera a Mia V3.`,
    controles: () => [{tipo: 'avancado', campo: 'usarFluxoTypebot', rotulo: 'Configurar o menu na tela avançada'}],
    explicacao: 'A criação e edição dos fluxos continuam na tela avançada.',
  },

  // ───────────── admin ─────────────
  {
    id: 'motor', titulo: 'Motor', emoji: '⚙️', cor: 'cinza', campos: ['usarMiaV3', 'carrinhoMiaV3'],
    texto: (v) => v.usarMiaV3
      ? `Atende pela Mia V3${campoDisponivel('conversaCurta', v) ? ' e monta e fecha pedidos no chat' : ', só tirando dúvidas e direcionando ao cardápio'}.`
      : 'Ainda atende pelo bot antigo.',
    controles: (v) => [
      {tipo: 'switch', campo: 'usarMiaV3', rotulo: 'Atender pela Mia V3'},
      v.usarMiaV3 && {tipo: 'switch', campo: 'carrinhoMiaV3', rotulo: 'Carrinho conversacional', dica: 'desligar esconde as frases de pedido acima'},
    ],
    explicacao: 'Substitui o bot antigo no WhatsApp oficial. Sem o carrinho, a Mia V3 só tira dúvidas e direciona ao cardápio.',
  },
  {
    id: 'modelo', titulo: 'Modelo de IA', emoji: '🧠', cor: 'cinza', campos: ['modeloIaV3'],
    texto: (v) => `Pensa com ${nomeDoModelo(v.modeloIaV3)} ⚙️.`,
    controles: () => [{tipo: 'escolha', campo: 'modeloIaV3', rotulo: 'Modelo de IA', opcoes: opcoesDoCampo('modeloIaV3')}],
    destaques: (v) => [nomeDoModelo(v.modeloIaV3)],
    explicacao: 'Vazio usa o padrão do serviço.',
  },
  {
    id: 'instrucoes', titulo: 'Instruções da loja', emoji: '📝', cor: 'cinza', campos: ['instrucoesDaLoja'],
    texto: (v) => v.instrucoesDaLoja ? `Segue instruções da loja: “${corta(v.instrucoesDaLoja, 60)}”.` : 'Sem instruções específicas da loja.',
    controles: () => [{tipo: 'area', campo: 'instrucoesDaLoja', rotulo: 'Instruções da loja (até 1500)', maxLength: 1500,
      placeholder: 'Ex.: A loja é tratada no masculino: “o Empório”. Nunca ofereça bebida alcoólica no almoço.'}],
    explicacao: 'Entram no prompt como instruções de estilo — as regras de segurança (nunca inventar itens, preços ou prazos) continuam valendo por cima.',
  },
];

/** O grupo e o acesso vêm dos campos do catálogo, não de uma segunda classificação. */
export function assuntosDoMomento(momento: MomentoDoJeito, vista: VistaDoJeito, ehAdmin = false): AssuntoDoJeito[] {
  return ASSUNTOS_DO_JEITO.filter((assunto) => {
    const campos = assunto.campos.map((chave) => campoDoCatalogo(chave)!);
    return campos[0].momento === momento && campos.every((campo) => campo.quemEdita !== 'admin' || ehAdmin)
      && campos.some((campo) => campoDisponivel(campo.chave, vista)) && (!assunto.so || assunto.so(vista));
  }).sort((a, b) => camposDoGrupo(a) - camposDoGrupo(b));
}

function camposDoGrupo(assunto: AssuntoDoJeito): number {
  return CATALOGO_CONFIG_MIA.findIndex((campo) => campo.chave === assunto.campos[0]);
}

/** Toda tela respeita a mesma cadeia de dependências. */
export function controlesDoAssunto(assunto: AssuntoDoJeito, vista: VistaDoJeito): ControleDoAssunto[] {
  return assunto.controles(vista).filter((c): c is ControleDoAssunto => !!c && campoDisponivel(c.campo, vista));
}
